// The guided setup wizard's STEP MANAGER: given the quote's sections and the owner's current setup state, it
// produces the exact ordered steps the wizard walks -- and ONLY the ones still needed. A material price, a
// machine, or a machine's price the owner already has is not a step; as each is added the manager is re-run with
// fresh state and that step drops out, so "Step X of N" always tells the truth about what is left.
//
// The steps, in order:
//   Stage 1  QUANTITY in order -- always, once, for the whole quote.
//   Stage 2  MANUFACTURING LINES SOURCE -- Standard vs My Manufacturing Lines, only for a user who has priced a
//            quote before (includeManufacturingLinesSource).
//   then, PER priceable nav SECTION that actually chooses (e.g. Carton), only the setup steps it is MISSING,
//   then its two choice steps:
//     material price          -- add a unit price for the section's material            (own lines, if unpriced)
//     printing machine        -- add the printing machine itself                        (own lines, if it prints
//                                                                                          and has none)
//     printing machine price  -- add that machine's unit price                          (own lines, if unpriced)
//     die-cutting machine     -- add the die-cutting machine itself                     (own lines, if none)
//     die-cutting machine price -- add that machine's unit price                        (own lines, if unpriced)
//     manufacturing line      -- pick one manufacturing line
//     solution                -- pick one solution  (the LAST step; leaving it, Done, closes the wizard)
//   A machine counts only if it can run the section's material thickness. When the owner's machines for the
//   part_type all fail that, the machine step is "your existing machines aren't suitable" instead -- add a new
//   machine, or edit one of those (machine_thickness_mismatch).
//
// PRICING IS NOT A STEP: once the last solution is chosen the wizard closes onto the page's Pricing tab, which is
// where the priced result is read. It is the OUTCOME of finishing, not a stage to walk, so it is not counted --
// a fully-unconfigured single Carton is 8 steps (quantity + its five setup steps + line + solution).
//
// A MACHINE is TWO steps -- adding the machine and pricing it -- because either can be already done on its own:
// a manufacturing line is FOUND from a machine that has no price, and it cannot be quoted until the price exists,
// so an owner who has the machine but not its price is led straight to the price step (the machine step is
// skipped). This is the reported case (a found, scored line the owner could never actually be quoted).
//
// TWO kinds of section are skipped entirely, both because they choose nothing:
//   * the nav builder's 'general' catch-all, which is not a manufacturing part section;
//   * a LINKED section, which takes its manufacturing line and solution from another section (see
//     linkedNavSection.js) -- walking someone through stages that decide nothing would imply the answer was
//     theirs to give.
//
// STANDARD manufacturing lines are never gated: they use the example account, which has every material and
// machine priced, so a Standard quote has no setup steps -- just quantity, the choice steps, and pricing.
//
// requirementsBySection, hasMaterialPrice and sectionMachine are passed IN rather than read from the window here, so
// this stays a pure function the caller can test at any state -- FrankSetupWizard reads the live sections, the
// server-seeded requirements and the live counts, and hands them over.

const OWN = 'own';
// The step that stands in for "Add a machine" when the owner's machines for the part_type cannot run the material's
// thickness (see sectionSetupSteps).
export const MACHINE_THICKNESS_MISMATCH = 'machine_thickness_mismatch';

// A corrugated_cardboard_offset section is LINKED to the carton_lamination section that leads it (see
// linkedNavSection.js / ManufacturingCostCalculatorNavBuilder#link_corrugated_offset_to_carton_lamination), so it
// has no line or solution of its own and the wizard skips it -- BUT it is a DIFFERENT material
// (corrugated_cardboard_material, not the lamination's carton_material), and nothing else in the guided flow
// prices it. See withLinkedCorrugatedMaterialStep.
const CORRUGATED_OFFSET_PART_TYPE = 'corrugated_cardboard_offset';
const CARTON_LAMINATION_PART_TYPE = 'carton_lamination';

// The setup steps ONE section is still missing, in the order they must be done: its material price, then for each
// machine row (printing before die-cutting) the machine itself and then its price -- each included only when the
// owner does not already have it. Empty on Standard, or once the section is fully set up.
const sectionSetupSteps = (requirement, sectionIndex, label, {
  source, hasMaterialPrice, sectionMachine
}) => {
  if (source !== OWN || !requirement) { return []; }

  const steps = [];
  // Server-seeded (Frank::SectionSetupRequirements), so snake_case: material_key, board_thickness_mm, and
  // machines[{ key, allowed_types, library_attribute_ids }].
  const materialKey = requirement.material_key;
  const machines = requirement.machines || [];

  if (materialKey && !hasMaterialPrice(materialKey)) {
    // board_thickness_mm / corrugated_cardboard_flute narrow the material step's frequently-used list to the
    // drawing's board (hard cardboard) or flute (corrugated cardboard); null otherwise leaves it unfiltered.
    steps.push({
      type: 'material', materialKey, boardThicknessMm: requirement.board_thickness_mm,
      corrugatedCardboardFlute: requirement.corrugated_cardboard_flute, sectionIndex, label,
    });
  }

  // sectionMachine answers per SECTION, counting only the owner's machines for this section's part_type: a hard
  // cardboard section is not served by a carton-only die-cutter, so its owner is walked through adding (and pricing)
  // one it can use. An owner who already HAS an unpriced machine for it is led to price THAT machine -- the price
  // step carries its id (attributeId), so the form opens on it rather than on the owner's first machine of the key.
  // Only machines that can run the section's material thickness count. When the owner HAS machines for the
  // part_type but none can run it, the add step becomes a machine_thickness_mismatch step: it says the existing
  // machines are not suitable and offers to add a new one or edit those. With no machine at all, the price step
  // follows the add (or mismatch) step and names no machine yet (shownStageIndex never shows it before that step is
  // done). libraryAttributeIds narrows the add step's library to the machines that would pass, so picking one cannot
  // bring the step straight back.
  machines.forEach(({ key: machineKey, allowed_types: allowedTypes, library_attribute_ids: libraryAttributeIds }) => {
    const {
      priced, unpricedAttributeId, materialThicknessMm, thicknessMismatchMachines = [],
    } = sectionMachine(sectionIndex, machineKey);
    if (priced) { return; }

    if (unpricedAttributeId == null) {
      const addStep = { machineKey, allowedTypes, libraryAttributeIds, sectionIndex, label };
      steps.push(thicknessMismatchMachines.length > 0
        ? { ...addStep, type: MACHINE_THICKNESS_MISMATCH, materialThicknessMm, thicknessMismatchMachines }
        : { ...addStep, type: 'machine_entry' });
    }
    steps.push({ type: 'machine_price', machineKey, attributeId: unpricedAttributeId ?? null, sectionIndex, label });
  });

  return steps;
};

// ONE extra material step for a LINKED corrugated_cardboard_offset section, added AFTER the carton_lamination
// section's solution stage. A linked corrugated section takes its manufacturing line and solution from the
// lamination it follows, so it is skipped above -- but it is laminated onto a corrugated board that is a separate,
// separately-priced material (corrugated_cardboard_material). On the owner's OWN lines the linked section then
// auto-prices that material from the first corrugated_cardboard_material price the owner has
// (Frank::OwnLinePricingDefaults); with NONE priced its material cost is silently missing and nothing in the
// guided flow prompts for one. So on own lines, when the owner has no such price yet, add a single material step
// so closing the wizard leaves the corrugated section priced. Standard lines use the fully-primed example account,
// so they never need it and are left exactly as they were.
const withLinkedCorrugatedMaterialStep = (stages, navSections, {
  linkedSectionIndexes, source, requirementsBySection, hasMaterialPrice,
}) => {
  if (source !== OWN) { return stages; }

  const sections = navSections || [];
  const corrugatedIndex = sections.findIndex(
    (section, index) => section.part_type === CORRUGATED_OFFSET_PART_TYPE && linkedSectionIndexes.includes(index)
  );
  const laminationIndex = sections.findIndex((section) => section.part_type === CARTON_LAMINATION_PART_TYPE);
  if (corrugatedIndex < 0 || laminationIndex < 0) { return stages; }

  // The corrugated section's requirement is server-seeded like every other section's (Frank::SectionSetupRequirements,
  // keyed by sort_order), so material_key is corrugated_cardboard_material and corrugated_cardboard_flute narrows the
  // material list to the box's flute. Already priced -> the section auto-prices, so no step (the honest-remaining-steps
  // rule the rest of the wizard follows).
  const requirement = requirementsBySection[corrugatedIndex];
  const materialKey = requirement && requirement.material_key;
  if (!materialKey || hasMaterialPrice(materialKey)) { return stages; }

  const section = sections[corrugatedIndex];
  const materialStage = {
    type: 'material',
    materialKey,
    boardThicknessMm: requirement.board_thickness_mm,
    corrugatedCardboardFlute: requirement.corrugated_cardboard_flute,
    sectionIndex: corrugatedIndex,
    label: section.label || section.part_type,
  };

  // "After finding a solution for carton_lamination" -- right after that section's solution stage. The lamination
  // section is never linked, so it is always walked and its solution stage is present; append as a fallback anyway.
  const laminationSolutionPosition = stages.findIndex(
    (stage) => stage.type === 'solution' && stage.sectionIndex === laminationIndex
  );
  if (laminationSolutionPosition < 0) { return [...stages, materialStage]; }

  return [
    ...stages.slice(0, laminationSolutionPosition + 1),
    materialStage,
    ...stages.slice(laminationSolutionPosition + 1),
  ];
};

// The full step list -- quantity, then each choosing section's missing setup steps followed by its line and
// solution. Every entry is a stage the wizard walks; the count (.length) is the "of N" the user sees.
export const buildSetupWizardStages = (navSections, {
  linkedSectionIndexes = [],
  source = 'standard',
  requirementsBySection = {},
  hasMaterialPrice = () => true,
  sectionMachine = () => ({ priced: true, unpricedAttributeId: null }),
  includeManufacturingLinesSource = false,
} = {}) => {
  const choosingSections = (navSections || [])
    .map((section, index) => ({ section, index }))
    // 'general' is the quote-wide pricing catch-all and 'inside_lamination' is a pricing-only section (no
    // manufacturing line, no solution) -- neither chooses a line or a solution, so the wizard has nothing to
    // ask of them.
    .filter(({ section }) => section.part_type !== 'general' && section.part_type !== 'inside_lamination')
    .filter(({ index }) => !linkedSectionIndexes.includes(index));

  const sectionStages = choosingSections.flatMap(({ section, index }) => {
    const label = section.label || section.part_type;
    return [
      ...sectionSetupSteps(requirementsBySection[index], index, label, { source, hasMaterialPrice, sectionMachine }),
      { type: 'line', sectionIndex: index, label },
      { type: 'solution', sectionIndex: index, label },
    ];
  });

  // A user who has priced a quote before is asked the Standard vs My Manufacturing Lines choice as its own stage,
  // so the choice is not missed.
  const stages = [
    { type: 'quantity' },
    ...(includeManufacturingLinesSource ? [{ type: 'manufacturing_lines_source' }] : []),
    ...sectionStages,
  ];

  return withLinkedCorrugatedMaterialStep(stages, navSections, {
    linkedSectionIndexes, source, requirementsBySection, hasMaterialPrice,
  });
};

// The index of the stage the wizard must SHOW when it is at `index`. A price step that names no machine is only
// reachable when the stage list shifted under the wizard -- e.g. a machine deleted from a later stage re-inserts an
// earlier section's machine steps -- because its row's "Add a machine" (or machine_thickness_mismatch) step always
// comes first. Show that step, never a price form opening on the owner's first machine of the key, which may be one
// the section cannot use.
export const shownStageIndex = (stages, index) => {
  const stage = stages[index];
  if (!stage || stage.type !== 'machine_price' || stage.attributeId != null) { return index; }

  const entryIndex = stages.findIndex((candidate) => (
    (candidate.type === 'machine_entry' || candidate.type === MACHINE_THICKNESS_MISMATCH)
      && candidate.sectionIndex === stage.sectionIndex
      && candidate.machineKey === stage.machineKey
  ));
  return entryIndex >= 0 ? entryIndex : index;
};

export default buildSetupWizardStages;
